import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface FigurineDetail {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  price_pln: number | null;
  image_url: string | null;
  is_published: boolean;
  is_featured: boolean;
  tags: string[];
  avg_rating: number;
  rating_count: number;
  category_slug: string | null;
}

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'model3d';
  order_index: number;
}

export interface RatingWithUser {
  id: string;
  stars: number;
  username: string | null;
  created_at: string;
}

export function useFigurineDetail(slug: string) {
  const [figurine, setFigurine] = useState<FigurineDetail | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [reviews, setReviews] = useState<RatingWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setNotFound(false);

      // 1. Fetch figurine from view (includes avg_rating, rating_count)
      const { data: fig, error: figErr } = await supabase
        .from('figurines_with_rating')
        .select(`
          id, title, slug, description, price_pln, image_url,
          is_published, is_featured, tags, avg_rating, rating_count,
          categories(slug)
        `)
        .eq('slug', slug)
        .single();

      if (figErr || !fig) {
        if (!cancelled) { setNotFound(true); setLoading(false); }
        return;
      }

      const detail: FigurineDetail = {
        id: fig.id,
        title: fig.title,
        slug: fig.slug,
        description: fig.description,
        price_pln: fig.price_pln,
        image_url: fig.image_url,
        is_published: fig.is_published,
        is_featured: fig.is_featured,
        tags: fig.tags ?? [],
        avg_rating: Number(fig.avg_rating ?? 0),
        rating_count: Number(fig.rating_count ?? 0),
        category_slug: (fig as any).categories?.slug ?? null,
      };

      // 2. Fetch media
      const { data: mediaData } = await supabase
        .from('figurine_images')
        .select('id, url, type, order_index')
        .eq('figurine_id', fig.id)
        .order('order_index');

      // 3. Fetch reviews
      const { data: reviewsData } = await supabase
        .from('ratings_with_username')
        .select('id, stars, username, created_at')
        .eq('figurine_id', fig.id)
        .order('created_at', { ascending: false });

      if (!cancelled) {
        setFigurine(detail);
        setMedia((mediaData ?? []) as MediaItem[]);
        setReviews((reviewsData ?? []) as RatingWithUser[]);
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [slug]);

  return { figurine, media, reviews, loading, notFound };
}
